// What the `action_ingredient_remove` event carries, and which portion-screen results
// count as a food being taken out of a meal (#4536).
//
// Why the event exists at all: when somebody reports "my food disappeared from a meal",
// nothing in the recorded data said whether the app lost the write or the person removed
// the row themselves. Adding a food was recorded; a food leaving was not. Every route that
// takes a food out now records one event per food, carrying the food's id and a `source`
// naming the route, so the two cases can be told apart.
//
// The decisions live in this module, away from the component, because the component has no
// test harness — the same reason `ingredient-add-telemetry.ts` next door is a plain module.

import { EditIngredientResult } from 'pages/edit-ingredient/edit-ingredient-interface';
import { Ingredient, isIngredientReal } from '@cf-shared/data-model/ingredient';
import { IngredientEditReturnContext } from 'components/ingredient-search/ingredient-add-telemetry';

/**
 * What the `foodId` property says when the row that left had no food behind it.
 *
 * A typed-in ("macro") row is entered by hand and names no food in the database, and a
 * half-formed row can reach here without a food id at all. The property stays a plain,
 * readable string in both cases: an empty value would show in Mixpanel as a food that
 * exists and simply has no name.
 */
export const NO_FOOD_ID = 'none';

/**
 * Which route took the food out of the meal, as the event's `source` property says it.
 *
 * Written as a closed list so a new removal route has to add its name here rather than
 * inventing a spelling that reads in Mixpanel as a route nobody recognises.
 *
 * One name is missing from the list that used to be in it: `remove-all`, the bulk clear. No
 * button in the app ever reached it, so the route was deleted and the name went with it -- a
 * closed list is only useful if every name in it is a route that can actually happen. Events
 * already recorded in Mixpanel still carry it, which is why the lexicon still explains what
 * it meant.
 */
export type IngredientRemovalSource = 'portion-screen-delete' | 'card-menu' | 'added-undo';

/** The food id to report for an ingredient that is leaving a meal. */
export function foodIdForTelemetry(ingredient: Ingredient | undefined): string {
  if (!ingredient || !isIngredientReal(ingredient)) return NO_FOOD_ID;
  return ingredient.foodId || NO_FOOD_ID;
}

/**
 * Does this portion-screen result take a food out of the meal?
 *
 * **This decides the REMOVAL, not just the event.** The caller both takes the food off the
 * list and records the removal on the strength of this answer, so widening it for a
 * reporting reason would change what leaves the user's meal. If you ever want to count a
 * different set of events than the set of foods that actually leave, that is a second
 * question and it needs a second function — do not loosen this one.
 *
 * Yes only when both hold:
 *
 *  - the result is a delete. A save leaves the food in the meal, and a split replaces one
 *    row with the foods it is made of — the split is already counted as an add, and counting
 *    it as a removal as well would make a recipe broken into its parts read as food going
 *    missing;
 *  - the row it deletes is actually in the meal. The portion screen offers its "Remove"
 *    button while the user is still confirming a food that has NOT gone into the meal yet,
 *    and that route carries `index === -1` (see `openEditIngredientModal`). Nothing leaves
 *    the meal there — the pending food is simply not added — so recording a removal would
 *    report food going missing when none did, naming no food at all.
 */
export function editResultRemovesIngredient(
  context: IngredientEditReturnContext | undefined,
  result: EditIngredientResult | undefined,
): boolean {
  if (!context || !result) return false;
  return result.action === 'delete' && context.index >= 0;
}
